/**
 * Theme handling. Three preferences:
 *
 *   - 'system' (default) follows the OS via `prefers-color-scheme`
 *   - 'light'  forces light
 *   - 'dark'   forces dark
 *
 * The preference is persisted in localStorage. The inline script in index.html
 * applies the initial class on `<html>` before React mounts so there's no flash;
 * this module keeps it in sync afterwards and re-applies when:
 *   - The user changes the preference (toggle)
 *   - The OS theme changes while the page is open and pref is 'system'
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'deployit-theme';

function readStored(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'light') return 'light';
  if (pref === 'dark') return 'dark';
  return systemPrefersDark() ? 'dark' : 'light';
}

function apply(resolved: ResolvedTheme): void {
  const html = document.documentElement;
  if (resolved === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
}

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  /** Cycle system → light → dark → system. */
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStored()));

  // Persist + re-apply whenever preference changes.
  useEffect(() => {
    if (preference === 'system') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, preference);
    const next = resolve(preference);
    apply(next);
    setResolved(next);
  }, [preference]);

  // Subscribe to OS-level theme changes; only act when preference is 'system'.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (preference === 'system') {
        const next: ResolvedTheme = mq.matches ? 'dark' : 'light';
        apply(next);
        setResolved(next);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((pref: ThemePreference) => setPreferenceState(pref), []);
  const cycle = useCallback(() => {
    setPreferenceState((p) => (p === 'system' ? 'light' : p === 'light' ? 'dark' : 'system'));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, cycle }),
    [preference, resolved, setPreference, cycle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
